/**
 * 转换设计师编辑器的字体、列表与花字素材，供素材面板和编辑器字体加载、文字渲染共用
 */
import { type IAdvancedTextMaterial, type IFont, type ListStyle } from '@meitu/saas-editor-core/es/version-types';
import { type ArtTextStruct, type ListStyleStruct } from '@meitu/saas-editor-ui/es/types';
import { getLocaleStore } from '@/i18n';
import { ArtTextMaterialRaw, ListStyleMaterialRaw } from './index.d';

export const toEditorFormat = (raw: any[]) => {
    return raw.map<IFont>((item) => ({
        id: item.id,
        default_name: item.name,
        name: item.name,
        font_name: item.font_name,
        zip_url: item.font_file,
        // 水印需要用到
        zip_file: item.zip_url,
        material_id: item.id,
        woff2_file_url: item.woff2_file,
        category_name: item.category_name,
        is_commercial: item.is_commercial,
    }));
};

/**
 * 将供稿接口列表素材转换为编辑器配置，保留 SVG 数字序号所需的字体和补零信息
 *
 * @param raw - 设计师接口返回的列表素材，material_conf 为 JSON 字符串
 * @returns 素材面板与文字渲染共用的列表样式
 */
export const transformListStyleMaterialRaw = (raw: ListStyleMaterialRaw) => {
    const { thumbnail } = raw;
    const style = raw['sequence_style'];
    const id = String(raw.id);

    // 字符型列表样式（非资源型）
    if (style !== 'custom') {
        const ret: ListStyleStruct = {
            id,
            type: 'char',
            style: style as Exclude<ListStyle, 'custom'>,
            thumbnail: {
                width: 0,
                height: 0,
                url: thumbnail,
            },
        };
        return ret;
    }

    // 图片、SVG类型列表样式
    const fileType = raw['file_type'];
    const conf = JSON.parse(raw['material_conf']);
    const isSVG = fileType === 2;

    const ret: ListStyleStruct = {
        id,
        type: isSVG ? 'svg' : 'image',
        style: 'custom',
        config: {
            id,
            autoIncrement: isSVG ? conf.autoIncrement : 'none',
            url: conf.url,
            fontColor: conf.fontColor,
            fontSize: conf.fontSize,
            baseContentTextSize: conf.baseContentTextSize,
            iconColors: isSVG ? conf.iconColors : [],
            iconWidth: conf.iconWidth,
            iconHeight: conf.iconHeight,
            scale: conf.scale,
        },
        thumbnail: {
            width: 0,
            height: 0,
            url: thumbnail,
        },
    };

    if (isSVG && conf.autoIncrement === 'decimal') {
        if (conf.fontId && conf.fontId !== '0') ret.config.fontId = String(conf.fontId);
        if (Reflect.has(conf, 'padZero')) ret.config.padZero = conf.padZero === 1;
    }

    return ret;
};

export const toWidgetFormat = (raw: any[]) => {
    const { $t } = getLocaleStore();

    return raw.map((item) => ({
        category_id: item.category_id,
        category_name: item.category_name || $t((v) => v.hooks.common),
        id: item.id,
        font_name: item.font_name,

        is_vip: '0',
        material_id: item.id,
        name: item.name,
        thumbnail: item.thumbnail?.url,
        thumbnail_text_dark: item.thumbnail_text_dark?.url,
        thumbnail_text_white: item.thumbnail_text_white?.url,

        zip_file: item.zip_url,
        zip_url: item.woff2_file || item.font_file, //原先定义的名字，后续统一用font_file就可以
        font_file: item.woff2_file || item.font_file,

        corner_mark: item.corner_mark,
        threshold: item.threshold,
        type_id: item.type_id,
        woff2_file_url: item.woff2_file,
        font_group_name: item.font_group_name,
        font_weight_value: item.font_weight_value,
        font_group_thumbnail_dark: item.font_group_thumbnail_dark?.url || '',
        font_group_thumbnail_white: item.font_group_thumbnail_white?.url || '',
    }));
};

export const transformArtTextMaterialRaw = (raw: ArtTextMaterialRaw): ArtTextStruct => {
    const materialId = String(raw.id);
    const config = JSON.parse(JSON.parse(raw['material_conf']).config) as IAdvancedTextMaterial;
    const corner_mark = raw['corner_mark'];

    config.id = materialId;

    return {
        id: materialId,
        config,
        thumbnail: raw['thumbnail'],
        cornerMark: {
            url: corner_mark,
            width: 60,
            height: 32,
        },
    };
};
